/**
 * Which notifications deserve a toast (#1093): those that ARRIVE while the
 * desktop is open, not the ones loaded with it.
 *
 * The first loaded list is recorded as seen. After that, an unread row whose
 * id was never seen gets a toast — but only while it is fresh: a re-sync
 * after a long disconnect (or a full reload after `SYNC`) can bring rows from
 * hours ago, and a burst of toasts about old news helps nobody.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { Notification } from '@/services/notification-service'

/** Rows older than this on arrival are listed, not toasted. */
export const TOAST_MAX_AGE_MS = 5 * 60 * 1000
/** At most this many toasts are stacked; older ones make room. */
export const MAX_TOASTS = 3

interface Options {
  notifications: Notification[]
  isLoading: boolean
  /** No toasts while true (e.g. the panel is open and shows the row anyway). */
  suppressed?: boolean
  /**
   * Whether this notification may raise a toast (the person's per-category
   * preference, #1095). A row that may not is still recorded as seen, so
   * switching toasts back on does not replay it.
   */
  allowed?: (notification: Notification) => boolean
  now?: () => number
}

export interface UseNotificationToastsReturn {
  toasts: Notification[]
  dismiss: (id: string) => void
}

export function useNotificationToasts({
  notifications,
  isLoading,
  suppressed = false,
  allowed,
  now = Date.now,
}: Options): UseNotificationToastsReturn {
  const [toasts, setToasts] = useState<Notification[]>([])
  const seenRef = useRef<Set<string> | null>(null)
  const nowRef = useRef(now)
  nowRef.current = now
  const allowedRef = useRef(allowed)
  allowedRef.current = allowed

  useEffect(() => {
    if (isLoading) return
    if (seenRef.current === null) {
      seenRef.current = new Set(notifications.map((n) => n.id))
      return
    }
    const seen = seenRef.current
    const arrivals = notifications.filter((n) => !seen.has(n.id))
    if (arrivals.length === 0) return
    for (const n of arrivals) seen.add(n.id)
    if (suppressed) return
    const cutoff = nowRef.current() - TOAST_MAX_AGE_MS
    const allow = allowedRef.current
    const fresh = arrivals.filter(
      (n) => !n.is_read && new Date(n.created_at).getTime() >= cutoff && (!allow || allow(n)),
    )
    if (fresh.length === 0) return
    setToasts((current) => [...fresh, ...current].slice(0, MAX_TOASTS))
  }, [notifications, isLoading, suppressed])

  // A toast whose notification was read or deleted elsewhere has nothing left to say.
  useEffect(() => {
    setToasts((current) => {
      const stillUnread = current.filter((toast) =>
        notifications.some((n) => n.id === toast.id && !n.is_read),
      )
      return stillUnread.length === current.length ? current : stillUnread
    })
  }, [notifications])

  // Opening the panel clears the stack: the panel lists those rows, and the
  // floating panel sits in the same corner as the toasts (#1129).
  useEffect(() => {
    if (suppressed) setToasts((current) => (current.length === 0 ? current : []))
  }, [suppressed])

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  return { toasts, dismiss }
}
